import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import { MRT_ColumnDef } from 'material-react-table';

import { PermissionsEnum } from '@/auth/interfaces';
import { useColumnsInstalacionesOT } from '@/erp/tecnico/orden-trabajo/shared/hooks';
import ResumenTrasladoTecnicoModal from '@/erp/tecnico/orden-trabajo/shared/components/tables/fotos/traslado/ResumenTrasladoTecnicoModal';
import {
  EstadosActivacionInstallOTEnumChoice,
  EstadosOrdenTrabajoEnumChoice,
  // EstadosProcesoEnumChoice,
  ROUTER_PATHS,
} from '@/shared';
import { useFetchOrdenTrabajos } from '@/shared/actions/erp';
import { GeneralTable, TableTabsScene } from '@/shared/components/ui';
import { TABLE_CONSTANTS } from '@/shared/constants/ui';
import {
  useCheckPermissions,
  useTableServerSideFiltering,
} from '@/shared/hooks';
import { useTableFilter } from '@/shared/hooks/useTableFilter';
import { OrdenTrabajo } from '@/shared/interfaces';
import { hasAllPermissions } from '@/shared/utils';

export type ActivacionInstalacionPageProps = {
  estadoOT?:EstadosOrdenTrabajoEnumChoice,
  estadoActivacion?:EstadosActivacionInstallOTEnumChoice,
  enableEdit?:boolean,
  defaultDateRange?:[string, string],
  // Scope opcional por tipo de servicio (mapea a agendamiento__tipo_orden_servicio
  // en backend). Undefined => comportamiento de instalación (sin filtrar por tipo).
  tipoOrdenServicio?: string,
  // Excluir por tipo de servicio (ej. sacar TRASLADO de instalaciones).
  tipoOrdenServicioExclude?: string,
  // URL base para la navegación al editar (por defecto instalación).
  baseNavUrl?: string,
  // Solo TRASLADOS: agrega una columna "Ver fotos" (inicio/finalización) que
  // abre un modal de solo lectura. Default false => instalaciones NO se afectan.
  showFotosTraslado?: boolean
};

const ActivacionInstalacionPage: React.FC<
  ActivacionInstalacionPageProps
> = ({
  estadoOT,
  estadoActivacion,
  enableEdit=true,
  defaultDateRange,
  tipoOrdenServicio,
  tipoOrdenServicioExclude,
  baseNavUrl = ROUTER_PATHS.activaciones.activacionesInstalacionNav,
  showFotosTraslado = false
}) => {
  useCheckPermissions(PermissionsEnum.api_view_ordentrabajo);
  const navigate = useNavigate();
  // Solo TRASLADOS: OT seleccionada para ver sus fotos en el modal read-only.
  const [fotosOt, setFotosOt] = useState<OrdenTrabajo | null>(null);
  const returnUrlActivacionInstallOTPage = baseNavUrl;
  // const URL_RECOORDINADA = ROUTER_PATHS.activaciones.activacionPendingRecoordinadaInstallOT
  // server side filters - colums table
  const { filterObject, columnFilters, setColumnFilters } =
    useTableServerSideFiltering();

  ///* table
  const {
    globalFilter,
    pagination,
    searchTerm,
    onChangeFilter,
    setPagination,
    onChangeRangeDate,
    cleanFilter,
  } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  ///* fetch data
  const {
    data: OrdensTrabajoPagingRes,
    isLoading,
    isRefetching,
  } = useFetchOrdenTrabajos({
    enabled: true,
    params: {
      page: pageIndex + 1,
      page_size: pageSize,
      identificacion: searchTerm,
      ...filterObject,
      filterByState: false,
      estado_orden_trabajo: estadoOT,
      estado_activacion: estadoActivacion,
      tipo_orden_servicio: tipoOrdenServicio,
      tipo_orden_servicio_exclude: tipoOrdenServicioExclude,
      fecha_instalacion_range_after: defaultDateRange?.[0] || undefined,
      fecha_instalacion_range_before: defaultDateRange?.[1] || undefined
    },
  });

  ///* handlers
  const onEdit = (ordentrabajo: OrdenTrabajo) => {
    if(estadoOT==EstadosOrdenTrabajoEnumChoice.ESPERA_RECOORDINADA){
      navigate(
        `${returnUrlActivacionInstallOTPage}/pendiente-recoordinada/${ordentrabajo.id_orden_trabajo}`
      );
    }else if (estadoActivacion === EstadosActivacionInstallOTEnumChoice.GESTIONADA) {
      navigate(
        `${returnUrlActivacionInstallOTPage}/gestionada/${ordentrabajo.id_orden_trabajo}`
      );
    }else{
      navigate(
        `${returnUrlActivacionInstallOTPage}/pendiente/${ordentrabajo.id_orden_trabajo}`
      );
    }

  };

  ///* columns
  const { activacionesInstalacionesColumns } = useColumnsInstalacionesOT();

  // Columna "Ver fotos" SOLO para traslados (no muta la lista compartida de
  // instalaciones: se concatena localmente y solo cuando showFotosTraslado).
  const columns = useMemo<MRT_ColumnDef<OrdenTrabajo>[]>(() => {
    if (!showFotosTraslado) return activacionesInstalacionesColumns;
    const fotosCol: MRT_ColumnDef<OrdenTrabajo> = {
      id: 'ver_fotos_traslado',
      header: 'Fotos',
      enableColumnFilter: false,
      enableSorting: false,
      size: 130,
      Cell: ({ row }) => (
        <Button
          size="small"
          variant="outlined"
          startIcon={<PhotoLibraryOutlinedIcon />}
          onClick={() => setFotosOt(row.original)}
        >
          Ver fotos
        </Button>
      ),
    };
    return [...activacionesInstalacionesColumns, fotosCol];
  }, [activacionesInstalacionesColumns, showFotosTraslado]);

  return (
    <TableTabsScene
      globalFilter={globalFilter}
      onChangeFilter={onChangeFilter}
      searchInputText="por identificación"
      onChangeRangeDate={onChangeRangeDate}
      cleanFilter={cleanFilter}
    >
      <GeneralTable<OrdenTrabajo>
        columns={columns}
        data={OrdensTrabajoPagingRes?.data?.data || []}
        isLoading={isLoading}
        isRefetching={isRefetching}
        // // filters - server side
        enableManualFiltering={true}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        // // search
        enableGlobalFilter={false}
        // // pagination
        pagination={pagination}
        onPaging={setPagination}
        rowCount={OrdensTrabajoPagingRes?.data?.count}
        // // actions ========
        enableEditing={enableEdit}
        enableColumnActions={enableEdit && hasAllPermissions([
          PermissionsEnum.api_change_ordentrabajo,
        ])}
        actionsColumnSize={TABLE_CONSTANTS.ACTIONCOLUMN_WIDTH}
        // crud
        canEdit={enableEdit && hasAllPermissions([PermissionsEnum.api_change_ordentrabajo])}
        onEdit={onEdit}
        canDelete={false}
      />

      {showFotosTraslado && fotosOt && (
        <ResumenTrasladoTecnicoModal
          open
          onClose={() => setFotosOt(null)}
          ordenTrabajo={fotosOt}
        />
      )}
    </TableTabsScene>
  );
};

export default ActivacionInstalacionPage;
